import { SpectrogramPoint } from '@ledium/types';
import { MixerChannel } from '@ledium/ui-spectrum-mixer';

/**
 * Finds optimal intensity ratios (0–1) for each channel so that
 * the mixed spectrum is as close as possible to the target recipe.
 *
 * Solves: minimize ||A·x − r||²  subject to  0 ≤ x[i] ≤ 1
 * where A[:,i] = channel[i] spectrum at full intensity, r = recipe spectrum.
 *
 * Uses projected coordinate-descent (Gauss-Seidel style) which converges
 * for this convex problem. The problem is always small (2–32 channels × 171
 * wavelengths), so this completes in well under a millisecond.
 *
 * Returns an array of intensity ratios in the same order as `channels`.
 */
function autoMixSpectra(recipe: SpectrogramPoint[], channels: MixerChannel[], unit: 'irradiance' | 'pfd'): number[] {
    const n = channels.length;
    const k = recipe.length;

    if (n === 0 || k === 0) {
        return [];
    }

    // Pre-compute AᵀA (n×n) and Aᵀr (n-vector)
    // A is the k×n matrix where column i is channel[i]'s spectrum.
    const AtA: number[][] = Array.from({ length: n }, (_, i) =>
        Array.from({ length: n }, (__, j) => {
            let sum = 0;
            for (let wl = 0; wl < k; wl++) {
                sum += channels[i].spectrogram[wl][unit] * channels[j].spectrogram[wl][unit];
            }
            return sum;
        })
    );

    const Atr: number[] = Array.from({ length: n }, (_, i) => {
        let sum = 0;
        for (let wl = 0; wl < k; wl++) {
            sum += channels[i].spectrogram[wl][unit] * recipe[wl][unit];
        }
        return sum;
    });

    // Step size: α = 1 / L where L = Lipschitz constant of ∇f = 2·λmax(AᵀA).
    // Bound λmax via Gershgorin: λmax ≤ max_i Σ_j |AᵀA[i][j]|, so α = 1 / (2 · maxRowSum).
    let maxRowSum = 0;
    for (let i = 0; i < n; i++) {
        let rowSum = 0;
        for (let j = 0; j < n; j++) {
            rowSum += Math.abs(AtA[i][j]);
        }
        if (rowSum > maxRowSum) {
            maxRowSum = rowSum;
        }
    }
    const alpha = maxRowSum > 0 ? 1 / (2 * maxRowSum) : 1;

    // Projected gradient descent, starting from 50% intensity
    const x = new Array<number>(n).fill(0.5);

    for (let iter = 0; iter < 5000; iter++) {
        let maxChange = 0;
        for (let i = 0; i < n; i++) {
            // Gradient of ||Ax - r||² w.r.t. x[i]: 2 * (AᵀAx - Aᵀr)[i]
            let grad = -Atr[i];
            for (let j = 0; j < n; j++) {
                grad += AtA[i][j] * x[j];
            }
            grad *= 2;

            const xi = Math.min(1, Math.max(0, x[i] - alpha * grad));
            if (Math.abs(xi - x[i]) > maxChange) {
                maxChange = Math.abs(xi - x[i]);
            }
            x[i] = xi;
        }
        if (maxChange < 1e-8) {
            break;
        }
    }

    return x;
}

export default autoMixSpectra;
